import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Star, MapPin, Phone, Globe, Clock, MessageCircle, Upload, ExternalLink, Edit, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const LOUNGE_PLACEHOLDER = "https://images.unsplash.com/photo-1574267432553-4b4628081c31?w=400&h=300&fit=crop";

const LoungeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  
  const [lounge, setLounge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [tempRating, setTempRating] = useState(0);
  const [showRatingSlider, setShowRatingSlider] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    fetchLounge();
    if (token) {
      fetchFavoriteStatus();
      fetchUserRating();
    }
    fetchPhotos();
  }, [id, token]);

  const fetchLounge = async () => {
    try {
      const res = await axios.get(`${API}/lounges/${id}`);
      setLounge(res.data);
    } catch (error) {
      toast.error('Failed to load lounge');
      navigate('/lounges');
    } finally {
      setLoading(false);
    }
  };

  const fetchFavoriteStatus = async () => {
    try {
      const res = await axios.get(`${API}/lounges/${id}/favorite`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(res.data.favorited);
    } catch (error) {
      console.error('Failed to fetch favorite status');
    }
  };

  const fetchUserRating = async () => {
    try {
      const res = await axios.get(`${API}/lounges/${id}/rating`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.rating !== null) {
        setUserRating(res.data.rating);
        setTempRating(res.data.rating);
      }
    } catch (error) {
      console.error('Failed to fetch user rating');
    }
  };

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/lounges/${id}/photos`);
      setPhotos(res.data);
    } catch (error) {
      console.error('Failed to fetch photos');
    }
  };

  const toggleFavorite = async () => {
    if (!token) {
      navigate('/auth?reason=lounges');
      return;
    }
    try {
      const res = await axios.post(`${API}/lounges/${id}/favorite`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(res.data.favorited);
      toast.success(res.data.favorited ? 'Added to favorites' : 'Removed from favorites');
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const handleRatingClick = () => {
    if (!token) {
      navigate('/auth?reason=lounges');
      return;
    }
    setShowRatingSlider(true);
  };

  const submitRating = async () => {
    try {
      await axios.post(`${API}/lounges/rate`, {
        lounge_id: id,
        rating: tempRating
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRating(tempRating);
      setShowRatingSlider(false);
      toast.success('Rating saved!');
      fetchLounge(); // Refresh to get updated average
    } catch (error) {
      toast.error('Failed to save rating');
    }
  };

  const handlePhotoUpload = async (e) => {
    if (!token) {
      navigate('/auth?reason=lounges');
      return;
    }
    
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API}/lounges/${id}/photos`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo uploaded!');
      fetchPhotos();
      fetchLounge();
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const openInMaps = () => {
    if (!lounge) return;
    const locationParts = [lounge.name, lounge.address, lounge.city];
    if (lounge.state) locationParts.push(lounge.state);
    if (lounge.country && lounge.country !== 'US') locationParts.push(lounge.country);
    const query = encodeURIComponent(locationParts.filter(Boolean).join(', '));
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Use Apple Maps on iOS (opens in Maps app), Google Maps elsewhere
    const mapsUrl = isIOS 
      ? `https://maps.apple.com/?q=${query}`
      : `https://www.google.com/maps/search/?api=1&query=${query}`;
    
    // Use window.location for PWA compatibility - opens in external browser/app
    window.location.href = mapsUrl;
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/lounges/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lounge deleted');
      navigate('/lounges');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete lounge');
    }
  };

  if (loading || !lounge) {
    return (
      <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-white">
        Loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      <div className="max-w-md mx-auto">
        {/* Back Button & Favorite */}
        <div className="flex items-center justify-between p-4">
          <button onClick={() => navigate(-1)} className="text-white">
            ← Back
          </button>
          <div className="flex items-center gap-3">
            {isAdmin && (
              <>
                <button 
                  onClick={() => setShowEditModal(true)}
                  className="text-white/50 hover:text-white"
                >
                  <Edit size={20} />
                </button>
                <button 
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 size={20} />
                </button>
              </>
            )}
            <button onClick={toggleFavorite}>
              <Heart className={isFavorite ? 'text-red-500 fill-red-500' : 'text-white/50'} />
            </button>
          </div>
        </div>

        {/* Main Image */}
        <div className="px-4 mb-4">
          <div className="aspect-video rounded-xl overflow-hidden bg-white/10">
            <img
              src={lounge.image_url || LOUNGE_PLACEHOLDER}
              alt={lounge.name}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = LOUNGE_PLACEHOLDER; }}
            />
          </div>
        </div>

        {/* Lounge Info */}
        <div className="px-4">
          <h1 className="text-2xl font-bold text-white mb-2">{lounge.name}</h1>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-1">
              <Star className="text-amber-500 fill-amber-500" size={20} />
              <span className="text-white text-lg font-bold">
                {lounge.average_rating?.toFixed(1) || '0.0'}
              </span>
            </div>
            <span className="text-white/50 text-sm">
              ({lounge.rating_count || 0} ratings)
            </span>
          </div>

          {/* Details */}
          <div className="space-y-3 mb-6">
            <button 
              onClick={openInMaps}
              className="flex items-start gap-3 text-left w-full hover:bg-white/5 p-2 -mx-2 rounded-lg transition-colors"
            >
              <MapPin className="text-amber-500 flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="text-white">{lounge.address}</p>
                <p className="text-white/50 text-sm">
                  {lounge.city}{lounge.state ? `, ${lounge.state}` : ''}{lounge.country && lounge.country !== 'US' ? `, ${lounge.country}` : ''}
                </p>
                <p className="text-amber-500 text-xs mt-1 flex items-center gap-1">
                  <ExternalLink size={12} /> Open in Maps
                </p>
              </div>
            </button>

            {lounge.phone && (
              <a href={`tel:${lounge.phone}`} className="flex items-center gap-3 text-white/80 hover:text-white">
                <Phone className="text-amber-500" size={18} />
                <span>{lounge.phone}</span>
              </a>
            )}

            {lounge.website && (
              <a href={lounge.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/80 hover:text-white">
                <Globe className="text-amber-500" size={18} />
                <span className="truncate">{lounge.website}</span>
              </a>
            )}

            {lounge.hours && (
              <div className="flex items-center gap-3">
                <Clock className="text-amber-500" size={18} />
                <span className="text-white/80">{lounge.hours}</span>
              </div>
            )}
          </div>

          {/* Rating Slider */}
          {showRatingSlider ? (
            <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
              <p className="text-white mb-3 text-center">Your Rating: <span className="text-amber-500 font-bold">{tempRating.toFixed(1)}</span></p>
              <Slider
                value={[tempRating]}
                onValueChange={(value) => setTempRating(value[0])}
                max={10}
                step={0.1}
                className="mb-4"
              />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowRatingSlider(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={submitRating} className="flex-1 bg-amber-600 hover:bg-amber-700">
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <Button
              onClick={handleRatingClick}
              className="w-full bg-amber-600 hover:bg-amber-700 mb-4"
            >
              <Star size={18} className="mr-2" />
              {userRating > 0 ? `Your Rating: ${userRating.toFixed(1)}` : 'Rate this Lounge'}
            </Button>
          )}

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <Button
              variant="outline"
              onClick={() => navigate(`/lounge/${id}/reviews`)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <MessageCircle size={18} className="mr-2" />
              Reviews
            </Button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full border-white/20 text-white hover:bg-white/10"
                asChild
              >
                <span>
                  <Upload size={18} className="mr-2" />
                  Add Photo
                </span>
              </Button>
            </label>
          </div>

          {/* Photos */}
          {photos.length > 0 && (
            <div className="mb-6">
              <h2 className="text-white font-semibold mb-3">Photos ({photos.length})</h2>
              <div className="grid grid-cols-3 gap-2">
                {photos.slice(0, 6).map((photo) => (
                  <div key={photo.id} className="aspect-square rounded-lg overflow-hidden bg-white/10">
                    <img
                      src={photo.image_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Added By */}
          {lounge.added_by_username && (
            <p className="text-white/30 text-sm text-center">
              Added by {lounge.added_by_username}
            </p>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowDeleteConfirm(false)}
        >
          <div className="bg-[#141416] border border-red-500/30 rounded-2xl w-full max-w-sm p-6">
            <h2 className="text-xl font-bold text-red-500 mb-4">Delete Lounge?</h2>
            <p className="text-white/70 mb-6">
              This will permanently delete "{lounge.name}" and all its ratings, photos, and comments. This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditLoungeModal
          lounge={lounge}
          token={token}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            fetchLounge();
          }}
        />
      )}

      <BottomNav />
    </div>
  );
};

// Edit Lounge Modal Component
const EditLoungeModal = ({ lounge, token, onClose, onSuccess }) => {
  const isInternationalLounge = lounge.country && !['US', 'USA', 'United States'].includes(lounge.country);
  const [isInternational, setIsInternational] = useState(isInternationalLounge);
  const [formData, setFormData] = useState({
    name: lounge.name || '',
    address: lounge.address || '',
    city: lounge.city || '',
    state: lounge.state || '',
    country: lounge.country || 'US',
    phone: lounge.phone || '',
    website: lounge.website || '',
    hours: lounge.hours || ''
  });
  const [submitting, setSubmitting] = useState(false);

  const US_STATES = [
    { abbr: 'AL', name: 'Alabama' }, { abbr: 'AK', name: 'Alaska' }, { abbr: 'AZ', name: 'Arizona' },
    { abbr: 'AR', name: 'Arkansas' }, { abbr: 'CA', name: 'California' }, { abbr: 'CO', name: 'Colorado' },
    { abbr: 'CT', name: 'Connecticut' }, { abbr: 'DE', name: 'Delaware' }, { abbr: 'FL', name: 'Florida' },
    { abbr: 'GA', name: 'Georgia' }, { abbr: 'HI', name: 'Hawaii' }, { abbr: 'ID', name: 'Idaho' },
    { abbr: 'IL', name: 'Illinois' }, { abbr: 'IN', name: 'Indiana' }, { abbr: 'IA', name: 'Iowa' },
    { abbr: 'KS', name: 'Kansas' }, { abbr: 'KY', name: 'Kentucky' }, { abbr: 'LA', name: 'Louisiana' },
    { abbr: 'ME', name: 'Maine' }, { abbr: 'MD', name: 'Maryland' }, { abbr: 'MA', name: 'Massachusetts' },
    { abbr: 'MI', name: 'Michigan' }, { abbr: 'MN', name: 'Minnesota' }, { abbr: 'MS', name: 'Mississippi' },
    { abbr: 'MO', name: 'Missouri' }, { abbr: 'MT', name: 'Montana' }, { abbr: 'NE', name: 'Nebraska' },
    { abbr: 'NV', name: 'Nevada' }, { abbr: 'NH', name: 'New Hampshire' }, { abbr: 'NJ', name: 'New Jersey' },
    { abbr: 'NM', name: 'New Mexico' }, { abbr: 'NY', name: 'New York' }, { abbr: 'NC', name: 'North Carolina' },
    { abbr: 'ND', name: 'North Dakota' }, { abbr: 'OH', name: 'Ohio' }, { abbr: 'OK', name: 'Oklahoma' },
    { abbr: 'OR', name: 'Oregon' }, { abbr: 'PA', name: 'Pennsylvania' }, { abbr: 'RI', name: 'Rhode Island' },
    { abbr: 'SC', name: 'South Carolina' }, { abbr: 'SD', name: 'South Dakota' }, { abbr: 'TN', name: 'Tennessee' },
    { abbr: 'TX', name: 'Texas' }, { abbr: 'UT', name: 'Utah' }, { abbr: 'VT', name: 'Vermont' },
    { abbr: 'VA', name: 'Virginia' }, { abbr: 'WA', name: 'Washington' }, { abbr: 'WV', name: 'West Virginia' },
    { abbr: 'WI', name: 'Wisconsin' }, { abbr: 'WY', name: 'Wyoming' }, { abbr: 'DC', name: 'Washington DC' }
  ];

  const handleToggleInternational = (intl) => {
    setIsInternational(intl);
    if (intl) {
      setFormData({ ...formData, country: formData.country === 'US' ? '' : formData.country });
    } else {
      setFormData({ ...formData, country: 'US' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address || !formData.city) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!isInternational && !formData.state) {
      toast.error('Please select a state');
      return;
    }

    if (isInternational && !formData.country) {
      toast.error('Please enter a country');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/lounges/${lounge.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lounge updated!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lounge');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#141416] w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#141416] flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Edit Lounge</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* US / International Toggle */}
          <div className="flex gap-2 mb-2">
            <button
              type="button"
              onClick={() => handleToggleInternational(false)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                !isInternational 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
            >
              US Lounge
            </button>
            <button
              type="button"
              onClick={() => handleToggleInternational(true)}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                isInternational 
                  ? 'bg-amber-600 text-white' 
                  : 'bg-white/10 text-white/60 hover:bg-white/20'
              }`}
            >
              International
            </button>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1 block">Lounge Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              required
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1 block">Street Address *</label>
            <Input
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/70 text-sm mb-1 block">City *</label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="bg-white/5 border-white/10 text-white"
                required
              />
            </div>
            {isInternational ? (
              <div>
                <label className="text-white/70 text-sm mb-1 block">Country *</label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g., Canada"
                  className="bg-white/5 border-white/10 text-white"
                  required
                />
              </div>
            ) : (
              <div>
                <label className="text-white/70 text-sm mb-1 block">State *</label>
                <select
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 text-white rounded-md"
                  required
                >
                  <option value="">Select</option>
                  {US_STATES.map(s => (
                    <option key={s.abbr} value={s.abbr}>{s.abbr} - {s.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {isInternational && (
            <div>
              <label className="text-white/70 text-sm mb-1 block">State/Province/Region</label>
              <Input
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="e.g., Ontario (optional)"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          )}

          <div>
            <label className="text-white/70 text-sm mb-1 block">Phone</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1 block">Website</label>
            <Input
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1 block">Hours</label>
            <Input
              value={formData.hours}
              onChange={(e) => setFormData({ ...formData, hours: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LoungeDetailPage;
